import {
    ASSIGN_TO_ADDON,
    FIND_ADDON_ASSIGNMENTS,
    useCurrentWorkspace,
    useCustomMutation,
    useCustomQuery,
} from '@gripp/shared-logic';
import {
    LoadingIndicator,
    SaveFormTop,
    TopModalBar,
    useGetDimensions,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import '../../shared/style/reactTabStyle.css';
import { AddonAssignmentSearchInput } from './addonAssignmentSearchInput';
import { AddonMediaAssignmentList } from './addonMediaAssignmentList';
import { AddonProductAssignmentList } from './addonProductAssignmentList';
import { AddonRoutinesAssignList } from './addonRoutinesAssignList';

interface Props {
  addonId: string;
  title: string;
  onComplete: () => void;
  initialTab?: 'product' | 'media' | 'routines';
}

export const AddonAssignmentForm = ({
  addonId,
  title,
  onComplete,
  initialTab,
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { dimensions, onLayout } = useGetDimensions();
  const height = dimensions?.height ?? 0;
  const listMaxHeight = height - 50;
  const currentWorkspace = useCurrentWorkspace();

  const getTabIndex = (tab?: 'product' | 'media' | 'routines') => {
    if (tab === 'product') return 1;
    if (tab === 'routines') return 2;
    return 0; // media
  };

  const [tabIndex, setTabIndex] = useState(getTabIndex(initialTab));

  useEffect(() => {
    if (initialTab) setTabIndex(getTabIndex(initialTab));
  }, [initialTab, addonId]);

  const [productAssignments, setProductAssignments] = useState<string[]>([]);
  const [originalProductAssignments, setOriginalProductAssignments] = useState<
    string[]
  >([]);

  const [mediaAssignments, setMediaAssignments] = useState<string[]>([]);
  const [originalMediaAssignments, setOriginalMediaAssignments] = useState<
    string[]
  >([]);

  const [routineAssignments, setRoutineAssignments] = useState<string[]>([]);
  const [originalRoutineAssignments, setOriginalRoutineAssignments] = useState<
    string[]
  >([]);

  const [keyword, setKeyword] = useState('');

  const { data: assignmentsData, isPending: isAssignmentsLoading } =
    useCustomQuery({
      query: FIND_ADDON_ASSIGNMENTS,
      cacheKey: ['addon', 'assignments', addonId],
      variables: { addonId },
    });

  useEffect(() => {
    const products =
      assignmentsData?.addon?.findAddonAssignments?.products || [];
    const media = assignmentsData?.addon?.findAddonAssignments?.media || [];
    const routines =
      assignmentsData?.addon?.findAddonAssignments?.routines || [];

    setOriginalProductAssignments(products);
    setProductAssignments(products);
    setOriginalMediaAssignments(media);
    setMediaAssignments(media);
    setOriginalRoutineAssignments(routines);
    setRoutineAssignments(routines);
  }, [addonId, assignmentsData]);

  const { mutateAsync: assignToAddon, isPending: isAssignLoading } =
    useCustomMutation({ query: ASSIGN_TO_ADDON });

  const handleSubmit = async () => {
    if (!currentWorkspace?.id) return;
    if (tabIndex === 0) {
      // media tab
      await assignToAddon({
        input: {
          addonId,
          workspaceId: currentWorkspace.id,
          mediaIds: mediaAssignments,
        },
      } as any);
    } else if (tabIndex === 1) {
      // product tab
      await assignToAddon({
        input: {
          addonId,
          workspaceId: currentWorkspace.id,
          productIds: productAssignments,
        },
      } as any);
    } else {
      // routines tab
      await assignToAddon({
        input: {
          addonId,
          workspaceId: currentWorkspace.id,
          routineIds: routineAssignments,
        },
      } as any);
    }
    queryClient.invalidateQueries({ queryKey: ['addon'], exact: false });
    onComplete();
  };

  const isProductsDirty = () =>
    originalProductAssignments.length !== productAssignments.length ||
    originalProductAssignments.some((p) => !productAssignments.includes(p)) ||
    productAssignments.some((p) => !originalProductAssignments.includes(p));
  const isMediaDirty = () =>
    originalMediaAssignments.length !== mediaAssignments.length ||
    originalMediaAssignments.some((m) => !mediaAssignments.includes(m)) ||
    mediaAssignments.some((m) => !originalMediaAssignments.includes(m));
  const isRoutinesDirty = () =>
    originalRoutineAssignments.length !== routineAssignments.length ||
    originalRoutineAssignments.some((r) => !routineAssignments.includes(r)) ||
    routineAssignments.some((r) => !originalRoutineAssignments.includes(r));
  const isDirty =
    tabIndex === 0
      ? isMediaDirty()
      : tabIndex === 1
      ? isProductsDirty()
      : isRoutinesDirty();

  const showLoading = isAssignmentsLoading || isAssignLoading; // single mutation pattern mirrors group form logic

  useEffect(() => {
    setKeyword('');
  }, [tabIndex]);

  return (
    <SaveFormTop
      onSubmit={handleSubmit}
      title={title}
      topBar={TopModalBar}
      onCancel={onComplete}
      isDirty={!isAssignLoading && isDirty}
    >
      {showLoading ? (
        <LoadingIndicator flexContainer />
      ) : (
        <View style={styles.container}>
          <AddonAssignmentSearchInput
            keyword={keyword}
            onChange={setKeyword}
            placeholder={
              tabIndex === 0
                ? 'mediaSearchPlaceholder'
                : tabIndex === 1
                ? 'productSearchPlaceholder'
                : 'routineSearchPlaceholder'
            }
          />
          <View style={styles.assignmentContainer} onLayout={onLayout}>
            <Tabs selectedIndex={tabIndex} onSelect={(i) => setTabIndex(i)}>
              <TabList>
                <Tab>{t('addOn.list.media')}</Tab>
                <Tab>{t('addOn.list.product')}</Tab>
                <Tab>{t('addOn.list.routines')}</Tab>
              </TabList>
              <View style={styles.listContainer}>
                <TabPanel>
                  {currentWorkspace?.id && (
                    <AddonMediaAssignmentList
                      workspaceId={currentWorkspace.id}
                      addonId={addonId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      mediaAssignments={mediaAssignments}
                      onMediaAssignmentsChange={setMediaAssignments}
                    />
                  )}
                </TabPanel>
                <TabPanel>
                  {currentWorkspace?.id && (
                    <AddonProductAssignmentList
                      workspaceId={currentWorkspace.id}
                      addonId={addonId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      assignments={productAssignments}
                      updateAssignments={setProductAssignments}
                    />
                  )}
                </TabPanel>
                <TabPanel>
                  {currentWorkspace?.id && (
                    <AddonRoutinesAssignList
                      workspaceId={currentWorkspace.id}
                      addonId={addonId}
                      maxHeight={listMaxHeight}
                      keyword={keyword}
                      assignments={routineAssignments}
                      updateAssignments={setRoutineAssignments}
                    />
                  )}
                </TabPanel>
              </View>
            </Tabs>
          </View>
        </View>
      )}
    </SaveFormTop>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: '100%',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 20,
  },
  assignmentContainer: { flex: 1, marginTop: 20 },
  listContainer: {
    borderTopRightRadius: 12,
    borderBottomRightRadius: 12,
    borderBottomLeftRadius: 12,
    overflow: 'hidden',
  },
});
